/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useInfiniteQuery, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getAssetHistory} from '@ion/core/src/services/ArcGIS';
import {GenericObject} from '@ion/core/src/types/entities';
import {AssetQueryParams} from '@ion/core/src/types/services';
import {createAssetsMap} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, filterPagesWithAssets, getAlignedRoutes, getLastTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseLoadHistoryAssetUpdatesResults = UseDefaultQueryResult & {
  results: object[];
  refresh: Function;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
};

export interface UseLoadHistoryAssetUpdates {
  assets: any;
  parentBaseKey: QueryKey;
  baseKey: QueryKey;
  enabled?: boolean;
  isTracking2?: boolean;
  parentQueryParams: AssetQueryParams;
  queryParams?: AssetQueryParams;
  refetchInterval?: number;
  propertyTime?: string;
  batchSize?: number;
}

/**
 * Custom React hook to load and paginate historical asset updates with automatic route matching and 
 * optional automatic fetching of next pages.
 * 
 * This hook uses React Query's `useInfiniteQuery` to fetch batches of asset history data,
 * supports automatic fetching of next pages, and aligns asset routes if enabled.
 * 
 * ```typescript
 *
 * import {useLoadHistoryAssetsV2} from '@ion/query';
 *
 * ...
 *
 * const {historyAssetsUpdates} = useLoadHistoryAssetsV2(params);
 *
 * ```
 * 
 * @param {UseLoadHistoryAssetUpdates} params - Parameters to configure the hook:
 *   - assets: Current asset list.
 *   - baseKey, parentBaseKey: Query keys for caching and invalidation.
 *   - routesHistory: Cached route history to help with route alignment.
 *   - queryParams, parentQueryParams: Query parameters for fetching asset history.
 *   - enabled: Flag to enable/disable the query.
 *   - isTracking2: Flag to toggle tracking mode.
 *   - refetchInterval: Optional interval for polling data.
 *   - batchSize: Number of assets to fetch per page.
 *   - propertyTime: Property name used to track the event time for assets.
 * 
 * @returns {UseLoadHistoryAssetUpdatesResults} - Returns query results including:
 *   - results: Array of assets with history and routes.
 *   - refresh: Function to manually refresh the data.
 *   - hasNextPage: Boolean indicating if there are more pages to fetch.
 *   - isFetchingNextPage: Boolean indicating if a next page fetch is in progress.
 */

export const useLoadHistoryAssetUpdatesV2 = ({
  assets,
  baseKey,
  queryParams,
  parentBaseKey,
  refetchInterval,
  parentQueryParams,
  isTracking2 = true,
  enabled = false,
  batchSize = 1,
  propertyTime = 'eventTime'
}: UseLoadHistoryAssetUpdates): UseLoadHistoryAssetUpdatesResults => {
  const lastTimeRef             = useRef(null);
  const assetsRef               = useRef(assets);
  const [lastTime, setLastTime] = useState(null);
  const queryClient             = useQueryClient();

  const parentkeys = useMemo(() => (
    createKey(parentBaseKey, parentQueryParams)
  ), [parentBaseKey, parentQueryParams]);
      
  const cachedData = queryClient.getQueryData(parentkeys) as GenericObject;

  useEffect(() => {
    assetsRef.current = assets;
  }, [assets]);
  
  useEffect(() => {
    setLastTime(null);
  }, [cachedData]);
  
  useEffect(() => {
    if (enabled) {
      const assets        = cachedData?.pages?.map(data => data.assets) || [] as any;
      lastTimeRef.current = lastTime || getLastTime(assets, propertyTime);
    }
  }, [cachedData, propertyTime, lastTime, enabled]);
  
  const keys = useMemo(() => (
    createKey(baseKey, queryParams)
  ), [baseKey, queryParams]);

  const assetList = useMemo(() => {
    return queryParams?.params?.assets || [];
  }, [queryParams]);

  const fetchAssets = useCallback(async({pageParam = 0}) => {
    if (lastTimeRef.current) {
      let previousAsset  = [];
      const start        = queryParams?.params?.start;
      const end          = queryParams?.params?.end;
      const geometry     = queryParams?.params?.geometry;
      const groups       = queryParams?.params?.groups;
      const startIndex   = pageParam * batchSize;
      const endIndex     = startIndex + batchSize;
      const currentBatch = assetList.slice(startIndex, endIndex);

      const params = {
        assets : currentBatch,
        geometry,
        groups,
        start,
        end
      };
    
      try {
        params.start    = new Date(lastTimeRef.current) as any;
        const assets    = await getAssetHistory(params, isTracking2);
        let assetRoutes = {};
        if (assets?.length) {
          const assetsMap = createAssetsMap(assets, assetsRef.current);
          assetRoutes     = await getAlignedRoutes(assetsMap, {});
        }
        const assetLength =  assets?.length || 0;
        if (assetLength) {
          previousAsset = assetsRef.current.filter(item1 =>
            assets.some(item2 => item2.assetId === item1.assetId)
          );
          setLastTime(getLastTime(assets as any, propertyTime));
        }
        return {
          assetRoutes,
          assets   : [...previousAsset, ...assets],
          nextPage : pageParam + 1
        };
      } catch (err) {
        error(err);
        return {
          assets      : [],
          assetRoutes : {},
          nextPage    : null
        };
      }
    }
  }, [queryParams, assetList, batchSize, isTracking2]);

  const query = useInfiniteQuery({
    queryKey         : keys,
    queryFn          : fetchAssets,
    getNextPageParam : (lastPage, allPages) => {
      const totalAssets  = assetList.length;
      const fetchedCount = allPages.length * batchSize;
      return fetchedCount < totalAssets ? lastPage.nextPage : undefined;
    },
    enabled,
    refetchInterval
  } as any);

  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = query;

  if (enabled && hasNextPage && !isFetchingNextPage) {
    fetchNextPage();
  }

  const pagesWithAssets = useMemo(() => filterPagesWithAssets(data), [data]);

  return createQueryResult(query, {
    results : pagesWithAssets || [],
    refresh : refetch,
    hasNextPage,
    isFetchingNextPage
  }) as UseLoadHistoryAssetUpdatesResults;
};
